/**
 * Timezone helpers for schedule pickers.
 *
 * The user's timezone lives in their profile settings (seeded from the browser at first
 * sign-in). Everything that shows or picks a wall-clock time reads it from there, so a
 * batch scheduled from a laptop in another zone still uses the hours the user works in.
 */

/** The browser's IANA zone, or 'UTC' when the runtime can't report one. */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The zone schedules are expressed in: the saved preference, falling back to the browser's. */
export function resolveUserTimezone(saved: string | null | undefined): string {
  return saved || getBrowserTimezone();
}

/** Every IANA zone the browser knows, for the settings picker. Falls back to a short list. */
export function listTimezones(): string[] {
  try {
    const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    const zones = supported?.('timeZone');
    if (zones?.length) return zones;
  } catch {
    // fall through to the minimal list below
  }
  const browser = getBrowserTimezone();
  const fallback = ['UTC', 'Europe/Paris', 'Europe/London', 'America/New_York', 'America/Los_Angeles'];
  return fallback.includes(browser) ? fallback : [browser, ...fallback];
}

/** Current UTC offset of a zone, e.g. "UTC+02:00", for labelling the picker. */
export function timezoneOffsetLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longOffset' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Render selected hours as the ranges they actually cover: [10,11,14,15] -> "10:00–12:00, 14:00–16:00".
 * Hour N means the hour starting at N:00, so a run ending at N closes at N+1:00.
 */
export function formatHourRanges(hours: number[]): string {
  const sorted = [...new Set(hours)].sort((a, b) => a - b);
  if (sorted.length === 0) return '';

  const pad = (h: number) => `${String(h).padStart(2, '0')}:00`;
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (const hour of sorted.slice(1)) {
    if (hour === prev + 1) {
      prev = hour;
      continue;
    }
    ranges.push(`${pad(start)}–${pad(prev + 1)}`);
    start = hour;
    prev = hour;
  }
  ranges.push(`${pad(start)}–${pad(prev + 1)}`);
  return ranges.join(', ');
}

/** Format an ISO instant as wall-clock time in the given zone, e.g. "Tue Sep 22, 14:30". */
export function formatInTimezone(iso: string, timezone: string): string {
  const date = new Date(iso);
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}
